/**
 * Zoom percentage — a pressable that opens the on-screen numpad dialog
 * (`NumberInputDialog`) instead of an inline `TextInput`. The native
 * number-pad keyboard wouldn't dismiss reliably over the floating floor
 * controls, so the modal numpad (Done / close buttons) owns entry. Idle it
 * mirrors the live zoom; opening seeds a draft; closing parses, clamps to the
 * engine zoom range, and jumps the camera. Isolated + memo'd so live zoom
 * updates re-render THIS leaf only, not the whole controls row.
 *
 * @module FloorPlanEditorZoomControlsPercentInput
 * @category Containers
 */

import { memo, useCallback, useState } from 'react';
import { Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';

import NumberInputDialog from '@components/NumberInputDialog';
import { Text } from '@components/ui/text';

import { MAX_ZOOM, MIN_ZOOM } from '@src/floorplan/constants';

type Props = {
  /** Current zoom, as a whole percentage — shown when the numpad is closed. */
  zoomPercent: number;
  /** Fired with the clamped, parsed percentage on numpad close. */
  onZoomTo: (percent: number) => void;
};

const MIN_PERCENT = Math.round(MIN_ZOOM * 100);
const MAX_PERCENT = Math.round(MAX_ZOOM * 100);

/**
 * Zoom percentage control — a pressable that opens the on-screen numpad
 * dialog instead of an inline text input; isolated + memo'd so live zoom
 * updates re-render this leaf only.
 *
 * @category Containers
 * @param zoomPercent - Current zoom, as a whole percentage.
 * @param onZoomTo - Fired with the clamped, parsed percentage.
 */
const FloorPlanZoomPercentInput = ({ zoomPercent, onZoomTo }: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const handleOpen = useCallback(() => {
    setDraft(String(zoomPercent));
    setOpen(true);
  }, [zoomPercent]);

  const handleClose = useCallback(() => {
    setOpen(false);
    const parsed = parseInt(draft, 10);
    if (Number.isNaN(parsed) || parsed <= 0) return;
    const clamped = Math.min(Math.max(parsed, MIN_PERCENT), MAX_PERCENT);
    onZoomTo(clamped);
  }, [draft, onZoomTo]);

  return (
    <>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('pages:floorPlanEditor.zoomLevel')}
        onPress={handleOpen}
        className="active:bg-muted h-11 flex-row items-center justify-center gap-0.5 rounded-xl px-2.5">
        <Text variant="titleSmall" className="text-foreground">
          {String(zoomPercent)}
        </Text>
        <Text
          variant="titleSmall"
          className="text-muted-foreground">{`%`}</Text>
      </Pressable>

      <NumberInputDialog
        open={open}
        onClose={handleClose}
        value={draft}
        onChangeText={setDraft}
      />
    </>
  );
};

export default memo(FloorPlanZoomPercentInput);
